import React, { useEffect, useMemo, useState } from "react";
import type { BudgetLineSaver } from "hooks/useBudget/useBudgetMutations";
import { BudgetCategory, BudgetFringeUnit } from "./types";
import { AgGridReact } from "ag-grid-react";
import { ColDef, SizeColumnsToContentStrategy } from "ag-grid-community";
import { useSearchParams } from "react-router-dom";
import classNames from "classnames";
import {
  getCategoryFringesInBudget,
  getFringeAccountNumber,
  isFringeUnitTimeBased,
} from "./BudgetItemUtils";
import { categoryFringesSearchParamName } from "./Budget";
import { DetailedBudgetBreadcrumbs } from "./DetailedBudgetBreadcrumbs";
import {
  AccountNumberMaxWidth,
  baseHeaderClasses,
  defaultColDef,
  RIVET_BUDGET_GRID_CLASS,
  RIVET_BUDGET_GRID_HEIGHT_CLASS,
  RIVET_BUDGET_GRID_WRAPPER_CLASS,
} from "./BudgetGridCommon";
import {
  budgetActualAmountClass,
  budgetEstimateAmountClass,
  budgetMonetaryCellClass,
  budgetVarianceAmountClass,
} from "./budgetGridFormatting";
import { CellRendererBase } from "./CellRendererBase";
import { budgetNonEditableReasons } from "config";
import { formatNumberWithCommas } from "utils";
import { EDITABLE_TABLE_ROW_HEIGHT } from "components/layout/table/tableStandard";

interface CategoryFringesGridProps {
  sheetData?: BudgetCategory[];
  categoryId: number;
  updateBudgetInfo: BudgetLineSaver;
}

interface FringeGridRow {
  name: string;
  account_number: string | null;
  rate: number;
  unit: BudgetFringeUnit;
  ceiling: number | null;
  ceiling_type: string;
  actual: number;
  estimate: number;
}

enum GridColumnIdentifiers {
  AccountNumber = "account_number",
  Name = "name",
  Rate = "rate",
  Unit = "unit",
  Ceiling = "ceiling",
  Estimate = "estimate",
  Actual = "actual",
  Variance = "variance",
}

export const CategoryFringesGrid: React.FC<CategoryFringesGridProps> = ({
  categoryId,
  sheetData,
  updateBudgetInfo: _updateBudgetInfo,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState<BudgetCategory>();

  useEffect(() => {
    setCategory(sheetData?.find((category) => category.budget_category_id === categoryId));
  }, [sheetData, categoryId]);

  const colDefs = useMemo<ColDef<FringeGridRow>[]>(
    () => [
      {
        headerName: "Acct #",
        field: "account_number",
        maxWidth: AccountNumberMaxWidth,
        colId: GridColumnIdentifiers.AccountNumber,
        cellClass: "w-full h-full",
        cellRenderer: (params: any) => (
          <CellRendererBase
            params={params}
            nonEditableReason={budgetNonEditableReasons.accountNumber}
          >
            <div>{params.data.account_number}</div>
          </CellRendererBase>
        ),
      },
      {
        headerName: "Name",
        field: "name",
        flex: 1,
        colId: GridColumnIdentifiers.Name,
        cellClass: "w-full h-full",
        cellRenderer: (params: any) => (
          <CellRendererBase
            params={params}
            nonEditableReason={budgetNonEditableReasons.fringesField}
          >
            <div>{params.data.name}</div>
          </CellRendererBase>
        ),
      },
      {
        headerName: "Rate",
        field: "rate",
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
        cellClass: budgetMonetaryCellClass,
        colId: GridColumnIdentifiers.Rate,
        cellRenderer: (params: any) => {
          const item = params.data;
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={budgetNonEditableReasons.fringesField}
            >
              <div className={budgetEstimateAmountClass()}>
                {formatNumberWithCommas(item.rate, true)}
              </div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "Unit",
        field: "unit",
        colId: GridColumnIdentifiers.Unit,
        cellClass: "w-full h-full",
        cellRenderer: (params: any) => {
          const item = params.data;
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={budgetNonEditableReasons.fringesField}
            >
              <div>{item.unit}</div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "Ceiling",
        field: "ceiling",
        colId: GridColumnIdentifiers.Ceiling,
        cellClass: "w-full h-full",
        cellRenderer: (params: any) => {
          const item = params.data;
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={budgetNonEditableReasons.fringesField}
            >
              <div>{item.ceiling}</div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "$ Estimate",
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
        cellClass: budgetMonetaryCellClass,
        colId: GridColumnIdentifiers.Estimate,
        colSpan: (params) => (isFringeUnitTimeBased(params.data!.unit) ? 2 : 1),
        cellRenderer: (params: any) => {
          const item = params.data;
          return (
            <CellRendererBase params={params} nonEditableReason={budgetNonEditableReasons.total}>
              <div className={budgetEstimateAmountClass()}>
                {formatNumberWithCommas(item.estimate, true)}
              </div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "$ Actual",
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
        cellClass: budgetMonetaryCellClass,
        colId: GridColumnIdentifiers.Actual,
        cellRenderer: (params: any) => {
          const item = params.data;
          return (
            <CellRendererBase params={params} nonEditableReason={budgetNonEditableReasons.total}>
              <div className={budgetActualAmountClass()}>
                {formatNumberWithCommas(item.actual, true)}
              </div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "$ Variance",
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
        cellClass: budgetMonetaryCellClass,
        colId: GridColumnIdentifiers.Variance,
        cellRenderer: (params: any) => {
          const item = params.data;
          const variance =
            item.actual && !isFringeUnitTimeBased(item.unit) ? item.estimate - item.actual : null;

          return (
            <CellRendererBase params={params} nonEditableReason={budgetNonEditableReasons.variance}>
              <div className={budgetVarianceAmountClass(variance)}>
                {variance != null ? formatNumberWithCommas(variance, true) : ""}
              </div>
            </CellRendererBase>
          );
        },
      },
    ],
    [sheetData]
  );

  const getDetailedBudgetRowData = () => {
    let path: { name: string; action: () => void }[] = [
      {
        name: "Top sheet",
        action: () => {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete(categoryFringesSearchParamName);
          setSearchParams(newSearchParams, { replace: true });
        },
      },
      {
        name: `${category?.name} FRINGES`,
        action: () => {},
      },
    ];

    const rowData = !category
      ? []
      : getCategoryFringesInBudget(category, sheetData).nested_fringes.map((fringe, index) => ({
          ...fringe,
          account_number: category.fringe_account_number
            ? getFringeAccountNumber(category.fringe_account_number, index)
            : null,
          name: fringe.name,
          unit: fringe.unit,
          rate: fringe.rate,
          ceiling: fringe.ceiling,
          ceiling_type: fringe.ceiling_type,
        }));

    return { rowData, path };
  };

  const { rowData: detailedBudgetRowData, path } = getDetailedBudgetRowData();

  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
    colIds: Object.values(GridColumnIdentifiers).filter(
      (col) => col !== GridColumnIdentifiers.Name
    ),
  };

  return (
    <div>
      <DetailedBudgetBreadcrumbs path={path} />
      <div className={classNames(RIVET_BUDGET_GRID_WRAPPER_CLASS, "mb-4")}>
        <div
          className={classNames(
            "ag-theme-quartz",
            RIVET_BUDGET_GRID_CLASS,
            RIVET_BUDGET_GRID_HEIGHT_CLASS,
            "budget-detail-grid"
          )}
          role="region"
          aria-label="Category fringes"
          style={{
            width: "100%",
          }}
        >
          <AgGridReact
            rowData={detailedBudgetRowData}
            autoSizeStrategy={autoSizeStrategy}
            columnDefs={colDefs}
            rowClass="bg-transparent text-foreground"
            rowHeight={EDITABLE_TABLE_ROW_HEIGHT}
            defaultColDef={defaultColDef}
          />
        </div>
      </div>
    </div>
  );
};
